import { type ActionReducerMapBuilder, createAction } from '@reduxjs/toolkit';

import type { CashById } from '@/store/cash/state.ts';
import type { FlexiForwardById } from '@/store/flexi-forward/state.ts';
import type { ForwardAccumulatorById } from '@/store/forwardAccumulator/state.ts';
import type { OptionById } from '@/store/option/state.ts';
import type { OrderById } from '@/store/orders/state.ts';
import type { SwapById } from '@/store/swap/state.ts';
import { tabsStateReducers } from '@/store/tabs/reducers.ts';
import type { Tab, TabsState } from '@/store/tabs/types.ts';
import type { TargetAccumulatorById } from '@/store/targetAccumulator/state.ts';
import { tilesAdapter } from '@/store/tiles/adapter.ts';
import type { TileState } from '@/store/tiles/slice.ts';

export const onSharedTabRemoved = createAction<Tab['id']>('onSharedTabRemoved');

export function reduceTabStateOnSharedTabRemoved(builder: ActionReducerMapBuilder<TabsState>) {
  builder.addCase(onSharedTabRemoved, (state, { payload: tabId }) => {
    tabsStateReducers.deleteTab(state, tabId);
  });
}

export function reduceTilesStateOnSharedTabRemoved(builder: ActionReducerMapBuilder<TileState>) {
  builder.addCase(onSharedTabRemoved, (state, { payload: tabId }) => {
    Object.values(state.entities).forEach((tile) => {
      if (tile.tabId === tabId) {
        tilesAdapter.removeOne(state, tile.tileId);
      }
    });
  });
}

export function reduceCashStateOnSharedTabRemoved(builder: ActionReducerMapBuilder<CashById>) {
  builder.addCase(onSharedTabRemoved, (state, { payload: tabId }) => {
    Object.keys(state).forEach((cashId) => {
      if (cashId === tabId) {
        delete state[cashId];
      }
    });
  });
}

export function reduceSwapStateOnSharedTabRemoved(builder: ActionReducerMapBuilder<SwapById>) {
  builder.addCase(onSharedTabRemoved, (state, { payload: tabId }) => {
    Object.keys(state).forEach((swapId) => {
      if (swapId === tabId) {
        delete state[swapId];
      }
    });
  });
}

export function reduceOptionStateOnSharedTabRemoved(builder: ActionReducerMapBuilder<OptionById>) {
  builder.addCase(onSharedTabRemoved, (state, { payload: tabId }) => {
    Object.keys(state).forEach((optionId) => {
      if (optionId === tabId) {
        delete state[optionId];
      }
    });
  });
}

export function reduceOrderStateOnSharedTabRemoved(builder: ActionReducerMapBuilder<OrderById>) {
  builder.addCase(onSharedTabRemoved, (state, { payload: tabId }) => {
    Object.keys(state).forEach((orderId) => {
      if (orderId === tabId) {
        delete state[orderId];
      }
    });
  });
}

export function reduceFWStateOnSharedTabRemoved(builder: ActionReducerMapBuilder<FlexiForwardById>) {
  builder.addCase(onSharedTabRemoved, (state, { payload: tabId }) => {
    Object.keys(state).forEach((flexiForwardId) => {
      if (flexiForwardId === tabId) {
        delete state[flexiForwardId];
      }
    });
  });
}

export function reduceTargetAccumulatorStateOnSharedTabRemoved(
  builder: ActionReducerMapBuilder<TargetAccumulatorById>,
) {
  builder.addCase(onSharedTabRemoved, (state, { payload: tabId }) => {
    Object.keys(state).forEach((targetAccumulatorId) => {
      if (targetAccumulatorId === tabId) {
        delete state[targetAccumulatorId];
      }
    });
  });
}

// to be discussed with team, something is broken in shared tab removal
export function reduceForwardAccumulatorStateOnSharedTabRemoved(
  builder: ActionReducerMapBuilder<ForwardAccumulatorById>,
) {
  builder.addCase(onSharedTabRemoved, (state, { payload: tabId }) => {
    Object.keys(state).forEach((forwardAccumulatorId) => {
      if (forwardAccumulatorId === tabId /* what ? */) {
        delete state[forwardAccumulatorId];
      }
    });
  });
}
